import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  Brain, 
  Clock, 
  ArrowRight, 
  SkipForward,
  CheckCircle2,
  XCircle,
  Sparkles,
  Target,
  ChevronRight,
  Lightbulb,
  Trophy
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DiagnosticTest = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [session, setSession] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [timeSpent, setTimeSpent] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [lastAnswerCorrect, setLastAnswerCorrect] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [results, setResults] = useState(null);
  
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    loadSession();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [sessionId]);

  useEffect(() => {
    // Start timer when new question loads
    if (currentQuestion && !completed) {
      startTimeRef.current = Date.now();
      timerRef.current = setInterval(() => {
        setTimeSpent(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
    }
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [currentQuestion?.id]);

  const loadSession = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/diagnostic/session/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.status === 'completed') {
        setCompleted(true);
        setResults(response.data.results);
      } else {
        setSession(response.data);
        setCurrentQuestion(response.data.currentQuestion);
      }
    } catch (error) {
      toast.error('Failed to load diagnostic session');
      navigate('/categories');
    }
    setLoading(false);
  };

  const submitAnswer = async (skipped = false) => {
    if (submitting) return;
    if (!skipped && selectedAnswer === null) {
      toast.error('Please select an answer');
      return;
    }
    
    setSubmitting(true);
    const finalTimeSpent = Math.floor((Date.now() - startTimeRef.current) / 1000);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/diagnostic/answer`, {
        sessionId,
        questionId: currentQuestion.id,
        selectedAnswer: skipped ? -1 : selectedAnswer,
        timeSpent: finalTimeSpent,
        skipped
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.status === 'completed') {
        setCompleted(true);
        setResults(response.data.results);
      } else {
        // Show brief feedback
        setLastAnswerCorrect(response.data.answeredCorrectly);
        setShowFeedback(true);
        
        setTimeout(() => {
          setShowFeedback(false);
          setSession(prev => ({
            ...prev,
            currentSubject: response.data.currentSubject,
            currentSubjectIndex: response.data.currentSubjectIndex,
            totalSubjects: response.data.totalSubjects,
            questionNumber: response.data.questionNumber,
            totalQuestionsInSubject: response.data.totalQuestionsInSubject
          }));
          setCurrentQuestion(response.data.currentQuestion);
          setSelectedAnswer(null);
          setTimeSpent(0);
          
          if (response.data.subjectChanged) {
            toast.success(`Great! Moving to ${response.data.currentSubject.replace('-', ' ')}`);
          }
        }, 800);
      }
    } catch (error) {
      toast.error('Failed to submit answer');
    }
    setSubmitting(false);
  };

  const generatePlan = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/diagnostic/generate-plan`, {
        sessionId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Your learning plan is ready!');
      navigate('/learning-plan');
    } catch (error) {
      toast.error('Failed to generate learning plan');
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-purple-600">Loading your assessment...</p>
        </div>
      </div>
    );
  }

  // Completion Screen
  if (completed && results) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-4 md:p-8">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="shadow-2xl border-0 overflow-hidden">
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white text-center">
                <Trophy className="h-16 w-16 mx-auto mb-4 text-yellow-300" />
                <h1 className="text-2xl font-bold mb-2">Assessment Complete!</h1>
                <p className="opacity-90">Great job! We now understand your learning profile.</p>
              </div>
              
              <CardContent className="p-6 space-y-6">
                {/* Summary Message */}
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 text-center">
                  <Sparkles className="h-8 w-8 text-amber-500 mx-auto mb-2" />
                  <p className="text-gray-700">{results.summary}</p>
                </div>
                
                {/* Subject Breakdown */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <Target className="h-5 w-5 text-purple-600" />
                    Your Strengths by Subject
                  </h3>
                  
                  {Object.entries(results.bands || {}).map(([subject, data]) => (
                    <div 
                      key={subject}
                      className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium capitalize">{subject.replace('-', ' ')}</span>
                        <Badge 
                          variant="outline" 
                          className={`
                            ${data.band === 'stretch' ? 'bg-green-100 text-green-700 border-green-300' : ''}
                            ${data.band === 'secure' ? 'bg-blue-100 text-blue-700 border-blue-300' : ''}
                            ${data.band === 'foundation' ? 'bg-amber-100 text-amber-700 border-amber-300' : ''}
                          `}
                        >
                          {data.accuracy}% accuracy
                        </Badge>
                      </div>
                      <Progress value={data.score} className="h-2" />
                      
                      {data.conceptsStrong?.length > 0 && (
                        <p className="text-xs text-green-600 mt-2">
                          ✓ Strong: {data.conceptsStrong.slice(0, 3).join(', ').replace(/_/g, ' ')}
                        </p>
                      )}
                      {data.conceptsWeak?.length > 0 && (
                        <p className="text-xs text-amber-600 mt-1">
                          ⚡ Focus: {data.conceptsWeak.slice(0, 3).join(', ').replace(/_/g, ' ')}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Action Button */}
                <div className="pt-4 space-y-3">
                  <Button
                    onClick={generatePlan}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 h-12 text-lg"
                    data-testid="generate-plan-btn"
                  >
                    {loading ? 'Creating your plan...' : (
                      <>
                        <Sparkles className="h-5 w-5 mr-2" />
                        Create My Learning Plan
                      </>
                    )}
                  </Button>
                  
                  <Button
                    variant="ghost"
                    onClick={() => navigate(-1)}
                    className="w-full"
                    data-testid="back-btn"
                  >
                    Go Back
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  // Question Screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-4 md:p-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Brain className="h-8 w-8 text-purple-600" />
              <div>
                <h1 className="text-xl font-bold text-gray-800">Learning Assessment</h1>
                <p className="text-sm text-gray-500 capitalize">{session?.currentSubject?.replace('-', ' ')}</p>
              </div>
            </div>
            <Badge variant="outline" className="bg-white">
              <Clock className="h-3 w-3 mr-1" />
              {timeSpent}s
            </Badge>
          </div>
          
          {/* Progress */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Subject {session?.currentSubjectIndex} of {session?.totalSubjects}</span>
              <span>Question {session?.questionNumber} of {session?.totalQuestionsInSubject}</span>
            </div>
            <Progress 
              value={(session?.questionNumber / session?.totalQuestionsInSubject) * 100} 
              className="h-2"
            />
          </div>
        </div>

        {/* Question Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion?.id}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="shadow-xl border-0 relative overflow-hidden">
              <CardHeader className="pb-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-purple-600">{session?.questionNumber}</span>
                  </div>
                  <CardTitle className="text-xl leading-relaxed" data-testid="question-text">
                    {currentQuestion?.question}
                  </CardTitle>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Answer Options */}
                <div className="space-y-3">
                  {currentQuestion?.options?.map((option, index) => (
                    <motion.button
                      key={index}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      onClick={() => setSelectedAnswer(index)}
                      disabled={submitting || showFeedback}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                        selectedAnswer === index
                          ? 'border-purple-500 bg-purple-50 shadow-md'
                          : 'border-gray-200 bg-white hover:border-purple-200 hover:bg-purple-50/30'
                      } ${showFeedback ? 'pointer-events-none' : ''}`}
                      data-testid={`answer-option-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${
                          selectedAnswer === index
                            ? 'bg-purple-500 text-white'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {String.fromCharCode(65 + index)}
                        </div>
                        <span className="text-gray-800">{option}</span>
                      </div>
                    </motion.button>
                  ))}
                </div>

                {/* Feedback Overlay */}
                <AnimatePresence>
                  {showFeedback && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      className={`absolute inset-0 flex items-center justify-center rounded-xl ${
                        lastAnswerCorrect ? 'bg-green-500/90' : 'bg-amber-500/90'
                      }`}
                    >
                      {lastAnswerCorrect ? (
                        <div className="text-center text-white">
                          <CheckCircle2 className="h-16 w-16 mx-auto mb-2" />
                          <p className="text-xl font-bold">Correct!</p>
                        </div>
                      ) : (
                        <div className="text-center text-white">
                          <Lightbulb className="h-16 w-16 mx-auto mb-2" />
                          <p className="text-xl font-bold">Keep going!</p>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => submitAnswer(true)}
                    disabled={submitting || showFeedback}
                    className="flex-1"
                    data-testid="skip-btn"
                  >
                    <SkipForward className="h-4 w-4 mr-2" />
                    Skip
                  </Button>
                  <Button
                    onClick={() => submitAnswer(false)}
                    disabled={submitting || selectedAnswer === null || showFeedback}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                    data-testid="submit-answer-btn"
                  >
                    {submitting ? 'Checking...' : (
                      <>
                        Next
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>

                {/* Encouragement */}
                <p className="text-center text-sm text-gray-500 pt-2">
                  💡 Take your time — this helps us understand your learning style
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default DiagnosticTest;
